import React, { useEffect, useState } from "react";
import { api, formatDate, formatApiErrorDetail, ALL_SECTIONS, SECTION_LABELS } from "../lib/api";
import { useAuth } from "../lib/auth";
import PageHeader from "../components/PageHeader";
import { Plus, X, Trash2, Lock, ShieldCheck } from "lucide-react";

const emptyForm = { email: "", password: "", name: "", role: "support", permissions: [] };

const ROLE_PRESETS = {
    super_admin: "*",
    admin: ALL_SECTIONS.filter((s) => s !== "team"),
    operations: ["dashboard", "live", "rides", "drivers", "customers"],
    support: ["tickets", "customers", "rides"],
};

const Team = () => {
    const { user } = useAuth();
    const [team, setTeam] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [err, setErr] = useState("");

    const isSuper = user?.role === "super_admin";

    const load = () => api.get("/team").then((r) => setTeam(r.data));
    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setForm({ ...emptyForm, permissions: ROLE_PRESETS.support });
        setEditing(null);
        setErr("");
        setShowForm(true);
    };

    const openEdit = (m) => {
        setForm({
            email: m.email,
            password: "",
            name: m.name,
            role: m.role,
            permissions: m.permissions || [],
        });
        setEditing(m);
        setErr("");
        setShowForm(true);
    };

    const togglePerm = (section) => {
        setForm((f) => {
            const has = f.permissions.includes(section);
            return { ...f, permissions: has ? f.permissions.filter((p) => p !== section) : [...f.permissions, section] };
        });
    };

    const applyRolePreset = (role) => {
        const preset = ROLE_PRESETS[role];
        const perms = preset === "*" ? ALL_SECTIONS.slice() : [...preset];
        setForm((f) => ({ ...f, role, permissions: perms }));
    };

    const handleSubmit = async () => {
        setErr("");
        try {
            if (editing) {
                const payload = { name: form.name, role: form.role, permissions: form.permissions };
                if (form.password) payload.password = form.password;
                await api.patch(`/team/${editing.id}`, payload);
            } else {
                await api.post("/team", form);
            }
            setShowForm(false);
            await load();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const handleToggle = async (m) => {
        await api.patch(`/team/${m.id}`, { active: !m.active });
        load();
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Remove this team member?")) return;
        await api.delete(`/team/${id}`);
        load();
    };

    const roleLabel = (r) => ({
        super_admin: "Super Admin",
        admin: "Admin",
        operations: "Operations",
        support: "Support",
    }[r] || r);

    return (
        <div className="space-y-6">
            <PageHeader
                title="Team Members"
                subtitle={`${team.length} members${!isSuper ? " · view-only" : ""}`}
                action={
                    isSuper && (
                        <button onClick={openCreate} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-mint-500 hover:bg-mint-600 text-white text-sm font-semibold" data-testid="team-add-btn">
                            <Plus className="w-4 h-4" /> Invite member
                        </button>
                    )
                }
            />

            {!isSuper && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-900 flex items-center gap-2">
                    <Lock className="w-4 h-4" /> Only super admins can invite or modify team members.
                </div>
            )}

            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                <th className="px-4 py-3">Name</th>
                                <th className="px-4 py-3">Email</th>
                                <th className="px-4 py-3">Role</th>
                                <th className="px-4 py-3">Access</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Joined</th>
                                {isSuper && <th className="px-4 py-3"></th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {team.map((m) => {
                                const perms = m.permissions || [];
                                const all = m.role === "super_admin" || perms.includes("*");
                                return (
                                    <tr key={m.id} className="hover:bg-slate-50" data-testid={`team-row-${m.email}`}>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-mint-400 to-mint-600 text-white flex items-center justify-center font-semibold">{m.name[0]?.toUpperCase()}</div>
                                                <div>
                                                    <div className="font-semibold text-navy-900">{m.name}</div>
                                                    {m.id === user?.id && <div className="text-[10px] text-mint-600 font-semibold">YOU</div>}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-slate-600">{m.email}</td>
                                        <td className="px-4 py-3">
                                            <span className="text-xs font-semibold text-slate-700 bg-slate-100 px-2 py-0.5 rounded-full border border-slate-200">{roleLabel(m.role)}</span>
                                        </td>
                                        <td className="px-4 py-3">
                                            {all ? (
                                                <span className="text-xs font-semibold text-mint-700 inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Full access</span>
                                            ) : (
                                                <div className="flex flex-wrap gap-1 max-w-md">
                                                    {perms.slice(0, 4).map((p) => (
                                                        <span key={p} className="text-[10px] font-semibold text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">{SECTION_LABELS[p] || p}</span>
                                                    ))}
                                                    {perms.length > 4 && (
                                                        <span className="text-[10px] text-slate-400">+{perms.length - 4} more</span>
                                                    )}
                                                    {perms.length === 0 && <span className="text-xs text-red-500">No access</span>}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            {m.active ? (
                                                <span className="text-xs font-semibold text-mint-700 bg-mint-50 px-2 py-0.5 rounded-full border border-mint-200">Active</span>
                                            ) : (
                                                <span className="text-xs text-slate-400">Disabled</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-slate-500">{formatDate(m.created_at)}</td>
                                        {isSuper && (
                                            <td className="px-4 py-3 text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    <button onClick={() => openEdit(m)} className="text-xs px-2.5 py-1 rounded-full border border-slate-200 hover:bg-slate-50" data-testid={`team-edit-${m.email}`}>
                                                        Edit
                                                    </button>
                                                    {m.id !== user?.id && (
                                                        <>
                                                            <button onClick={() => handleToggle(m)} className="text-xs px-2.5 py-1 rounded-full border border-slate-200 hover:bg-slate-50" data-testid={`team-toggle-${m.email}`}>
                                                                {m.active ? "Disable" : "Enable"}
                                                            </button>
                                                            <button onClick={() => handleDelete(m.id)} className="text-red-500 hover:bg-red-50 p-1.5 rounded-lg" data-testid={`team-delete-${m.email}`}>
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {showForm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setShowForm(false)}>
                    <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden relative" onClick={(e) => e.stopPropagation()} data-testid="team-form">
                        <button onClick={() => setShowForm(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 z-10" data-testid="team-form-close"><X className="w-5 h-5" /></button>
                        <div className="overflow-y-auto p-6 sm:p-8">
                            <h3 className="font-display font-bold text-xl text-navy-900 mb-1 pr-8">{editing ? "Edit team member" : "Invite a team member"}</h3>
                            <p className="text-sm text-slate-500 mb-5">{editing ? "Update role & section access. Leave password blank to keep current." : "They'll log in with the email & password you set here."}</p>
                            <div className="space-y-3">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Full name</label>
                                        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg" data-testid="team-form-name" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Email</label>
                                        <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editing} className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg disabled:opacity-60" data-testid="team-form-email" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{editing ? "New password" : "Temporary password"}</label>
                                        <input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editing ? "Leave blank to keep" : ""} className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg font-mono" data-testid="team-form-password" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Role preset</label>
                                        <select value={form.role} onChange={(e) => applyRolePreset(e.target.value)} className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg" data-testid="team-form-role">
                                            <option value="super_admin">Super Admin</option>
                                            <option value="admin">Admin</option>
                                            <option value="operations">Operations</option>
                                            <option value="support">Support</option>
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">Section access</label>
                                        <div className="text-[11px] text-slate-400">Tick what this user can open</div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 bg-slate-50 border border-slate-200 rounded-xl p-3">
                                        {ALL_SECTIONS.map((s) => {
                                            const isChecked = form.role === "super_admin" || form.permissions.includes(s);
                                            const disabled = form.role === "super_admin";
                                            return (
                                                <label key={s} className={`flex items-center gap-2 text-sm cursor-pointer px-2 py-1 rounded hover:bg-white transition-colors ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isChecked}
                                                        disabled={disabled}
                                                        onChange={() => togglePerm(s)}
                                                        className="rounded text-mint-500 focus:ring-mint-500"
                                                        data-testid={`team-form-perm-${s}`}
                                                    />
                                                    <span className="text-navy-900">{SECTION_LABELS[s]}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                                {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}
                                <button onClick={handleSubmit} className="w-full py-2.5 rounded-full bg-mint-500 hover:bg-mint-600 text-white font-semibold text-sm" data-testid="team-form-submit">
                                    {editing ? "Save changes" : "Invite member"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Team;
